<!-- Floating Action Button -->
<button
#assistantFab
class="fab-btn"
mat-mini-fab
color="primary"
[style.left.px]="fabLeft"
[style.top.px]="fabTop"
[hidden]="isOpen"
(pointerdown)="onFabPointerDown($event)"
(pointermove)="onFabPointerMove($event)"
(pointerup)="onFabPointerUp()"
(pointercancel)="onFabPointerUp()"
(click)="togglePanel()"
[matTooltip]="isOpen ? 'Close Assistant' : 'Workflow Assistant'"
aria-label="Workflow Assistant"
>
<mat-icon>{{ isOpen ? "close" : "smart_toy" }}</mat-icon>
</button>

<!-- Chat Panel -->
<div
  class="chat-panel"
  [class.open]="isOpen"
  role="dialog"
  aria-label="Workflow Assistant Chat"
  [attr.aria-hidden]="!isOpen"
>
  <!-- Header -->
  <div class="chat-header">
    <mat-icon class="header-icon">smart_toy</mat-icon>
    <span class="header-title">Workflow Assistant</span>
    <span class="header-subtitle">ASCC • AI-assisted</span>
    <!-- Give SA users a persistent path to submit and triage new assistant questions. -->
    <button
      *ngIf="canManageSuggestions"
      mat-icon-button
      (click)="openSuggestions()"
      matTooltip="Review assistant feedback"
      aria-label="Review assistant feedback"
    >
      <mat-icon>tips_and_updates</mat-icon>
    </button>
    <button
      mat-icon-button
      class="clear-btn"
      (click)="clearChat()"
      matTooltip="Clear chat"
      aria-label="Clear chat"
    >
      <mat-icon>delete_sweep</mat-icon>
    </button>
    <button mat-icon-button (click)="togglePanel()" aria-label="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Keep the assistant's role and the source of workflow decisions clear. -->
  <div class="assistant-disclaimer" role="note">
    <mat-icon>verified_user</mat-icon>
    <span><strong>A helpful guide, not the final word.</strong> CBorg interprets wording; ASCC supplies authorized information. Verify critical details before signing.</span>
  </div>

  <!-- Keep local assistance available while clearly identifying a CBorg service interruption. -->
  <div class="ai-availability" *ngIf="cborgUnavailable$ | async" role="status">
    <mat-icon>cloud_off</mat-icon>
    <span><strong>AI interpretation unavailable.</strong> Local ASCC help still works.</span>
  </div>

  <!-- Offer a page-aware export only when the current view contains rendered table data. -->
  <div class="table-export-offer" *ngIf="visibleTableExport">
    <mat-icon>table_view</mat-icon>
    <div class="table-export-copy">
      <strong>Table available</strong>
      <span>{{ visibleTableExport.rows.length }} visible row(s)</span>
    </div>
    <button
      mat-stroked-button
      class="table-export-btn"
      (click)="exportVisibleTable()"
      matTooltip="Export the visible table to Excel"
    >
      <mat-icon>download</mat-icon>
      Excel
    </button>
  </div>

  <!-- Message List -->
  <div class="message-list" #messageList>
    <div
      *ngFor="let msg of messages"
      class="message-row"
      [class.user-row]="msg.sender === 'user'"
      [class.assistant-row]="msg.sender === 'assistant'"
    >
      <!-- Assistant avatar -->
      <div class="avatar" *ngIf="msg.sender === 'assistant'">
        <mat-icon>smart_toy</mat-icon>
      </div>

      <div class="message-content" [class.user-message-content]="msg.sender === 'user'">
      <!-- Bubble -->
      <div
        class="bubble"
        [class.user-bubble]="msg.sender === 'user'"
        [class.assistant-bubble]="msg.sender === 'assistant'"
        [class.error-bubble]="msg.isError"
        [class.loading-bubble]="msg.isLoading"
      >
        <!-- Loading indicator -->
        <div *ngIf="msg.isLoading" class="typing-indicator">
          <span></span><span></span><span></span>
        </div>

        <!-- Message content -->
        <div
          *ngIf="!msg.isLoading"
          class="bubble-text"
          (click)="onContentClick($event)"
          [innerHTML]="formatContent(msg.content)"
        ></div>

        <!-- Action links -->
        <div *ngIf="msg.links?.length" class="bubble-links">
          <button
            *ngFor="let link of msg.links"
            mat-stroked-button
            class="link-btn"
            (click)="navigateTo(link.route)"
          >
            <mat-icon>open_in_new</mat-icon>
            {{ link.label }}
          </button>
        </div>
      </div>

      <!-- Keep feedback actions outside the bubble so they remain aligned and unobtrusive. -->
      <div class="answer-actions" *ngIf="msg.sender === 'assistant' && msg.sourceQuestion && !msg.isLoading && !msg.isError">
        <button
          mat-icon-button
          type="button"
          class="report-answer-btn"
          (click)="reportIncorrectAnswer(msg)"
          [disabled]="msg.feedbackSubmitted"
          [matTooltip]="msg.feedbackSubmitted ? 'Incorrect answer reported' : 'Report incorrect answer'"
          [attr.aria-label]="msg.feedbackSubmitted ? 'Incorrect answer reported' : 'Report incorrect answer'"
        >
          <mat-icon>{{ msg.feedbackSubmitted ? 'check_circle' : 'thumb_down' }}</mat-icon>
        </button>
      </div>
      </div>
    </div>
  </div>

  <!-- Quick Actions (shown when no messages or first message) -->
  <div class="quick-actions" *ngIf="messages.length <= 1 && !isThinking">
    <span class="quick-label">Try asking:</span>
    <button
      *ngFor="let action of QUICK_ACTIONS"
      mat-stroked-button
      class="quick-btn"
      (click)="onQuickAction(action)"
    >
      {{ action }}
    </button>
  </div>

  <div class="pending-questions" *ngIf="pendingQuestions.length">
    <div class="pending-title">Queued</div>
    <div class="pending-question" *ngFor="let question of pendingQuestions">
      <ng-container *ngIf="question.isEditing; else pendingText">
        <input
          class="pending-edit"
          [(ngModel)]="question.draftText"
          (keydown.enter)="savePendingQuestion(question)"
          (keydown.escape)="cancelPendingEdit(question)"
          aria-label="Edit queued question"
        />
        <button
          mat-icon-button
          class="pending-btn"
          (click)="savePendingQuestion(question)"
          [disabled]="!question.draftText.trim()"
          matTooltip="Save"
          aria-label="Save queued question"
        >
          <mat-icon>check</mat-icon>
        </button>
        <button
          mat-icon-button
          class="pending-btn"
          (click)="cancelPendingEdit(question)"
          matTooltip="Cancel"
          aria-label="Cancel edit"
        >
          <mat-icon>close</mat-icon>
        </button>
      </ng-container>

      <ng-template #pendingText>
        <span class="pending-text">{{ question.text }}</span>
        <button
          mat-icon-button
          class="pending-btn"
          (click)="editPendingQuestion(question)"
          matTooltip="Edit"
          aria-label="Edit queued question"
        >
          <mat-icon>edit</mat-icon>
        </button>
      </ng-template>

      <button
        mat-icon-button
        class="pending-btn delete"
        (click)="deletePendingQuestion(question)"
        matTooltip="Delete"
        aria-label="Delete queued question"
      >
        <mat-icon>delete</mat-icon>
      </button>
    </div>
  </div>

  <!-- Input Area -->
  <div class="chat-input-area">
    <mat-form-field appearance="outline" class="input-field">
      <input
        matInput
        #inputField
        [(ngModel)]="inputText"
        (keydown)="onKeyDown($event)"
        placeholder="Ask about a form, approval, status…"
        autocomplete="off"
      />
      <button
        matSuffix
        type="button"
        class="input-help-btn"
        (click)="showHelp()"
        matTooltip="Show Assistant help"
        aria-label="Show Assistant help"
      >
        <mat-icon>help</mat-icon>
      </button>
    </mat-form-field>
    <button
      mat-icon-button
      class="send-btn"
      color="primary"
      (click)="sendMessage()"
      [disabled]="!inputText.trim()"
      aria-label="Send"
    >
      <mat-icon>send</mat-icon>
    </button>
  </div>
</div>
